import { supabase, Tables } from '../config/supabase';
import { SNSConnection, SNSConnectionInput, SNSType } from '../types/sns';
import { ExtendedUserProfile as UserProfile, SNSConnectionData } from '../types/user';

// Export types
export type { UserProfile, SNSConnection, SNSConnectionInput };

// SNS Connection Functions
export const getSNSConnections = async (userId: string): Promise<SNSConnection[]> => {
  const { data: user, error } = await supabase
    .from('users')
    .select('sns_connections')
    .eq('id', userId)
    .single();

  if (error || !user) {
    return [];
  }

  const snsConnections = user.sns_connections || {};
  
  // SNS 연결을 기존 형태로 변환
  return (Object.entries(snsConnections) as [SNSType, SNSConnectionData][]).map(([type, data]) => ({
    id: `${userId}_${type}`,
    type: type as SNSType,
    userId,
    url: data.url,
    username: data.username,
    connected: data.connected,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt
  }));
};

export const connectSNS = async (
  userId: string,
  connectionData: SNSConnectionInput
): Promise<SNSConnection> => {
  const now = Date.now();
  
  const newConnectionData: SNSConnectionData = {
    url: connectionData.url,
    username: connectionData.username,
    connected: true,
    createdAt: now,
    updatedAt: now
  };

  // 현재 사용자 데이터 가져오기
  const { data: user, error: fetchError } = await supabase
    .from('users')
    .select('sns_connections')
    .eq('id', userId)
    .single();

  if (fetchError) {
    throw new Error('User not found');
  }

  const currentSNSConnections = user?.sns_connections || {};
  const updatedSNSConnections = {
    ...currentSNSConnections,
    [connectionData.type]: newConnectionData
  };

  const { error } = await supabase
    .from('users')
    .update({
      sns_connections: updatedSNSConnections,
      updated_at: new Date().toISOString()
    })
    .eq('id', userId);

  if (error) {
    throw error;
  }

  return {
    id: `${userId}_${connectionData.type}`,
    type: connectionData.type,
    userId,
    url: connectionData.url,
    username: connectionData.username,
    connected: true,
    createdAt: now,
    updatedAt: now
  };
};

export const disconnectSNS = async (connectionId: string): Promise<void> => {
  const [userId, snsType] = connectionId.split('_');
  
  const { data: user, error: fetchError } = await supabase
    .from('users')
    .select('sns_connections')
    .eq('id', userId)
    .single();

  if (fetchError || !user) {
    throw new Error('User not found');
  }

  const snsConnections = { ...(user.sns_connections || {}) };
  delete snsConnections[snsType as SNSType];

  const { error } = await supabase
    .from('users')
    .update({
      sns_connections: snsConnections,
      updated_at: new Date().toISOString()
    })
    .eq('id', userId);

  if (error) {
    throw error;
  }
};

// Interfaces
export interface UserSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  marketingEmails: boolean;
  darkMode: boolean;
}

export interface Campaign {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  category: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'completed' | 'pending';
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

export interface Review {
  id: string;
  campaignId: string;
  userId: string;
  content: string;
  rating: number;
  images: string[];
  createdAt: number;
  updatedAt: number;
}

// User related functions
export const createUserProfile = async (userId: string, data: Partial<UserProfile> = {}): Promise<void> => {
  const now = new Date().toISOString();
  const defaultProfile = {
    id: userId,
    email: data.email || '',
    display_name: data.displayName || '',
    photo_url: data.photoURL || '',
    user_type: data.userType || 'reviewer',
    created_at: now,
    updated_at: now,
    sns_connections: data.snsConnections || {}
  };

  const { error } = await supabase
    .from('users')
    .insert(defaultProfile);

  if (error) {
    throw error;
  }
};

export const getUserProfile = async (userId: string): Promise<UserProfile> => {
  const { data: user, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();

  if (error || !user) {
    // 사용자가 없으면 기본 프로필 생성
    const now = new Date().toISOString();
    const defaultProfile = {
      id: userId,
      email: '',
      display_name: '',
      photo_url: '',
      user_type: 'reviewer' as const,
      created_at: now,
      updated_at: now,
      sns_connections: {}
    };

    const { error: insertError } = await supabase
      .from('users')
      .insert(defaultProfile);

    if (insertError) {
      throw insertError;
    }

    return {
      uid: userId,
      email: '',
      displayName: '',
      photoURL: '',
      userType: 'reviewer',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      snsConnections: {} as any
    };
  }

  return {
    uid: user.id,
    email: user.email,
    displayName: user.display_name,
    photoURL: user.photo_url,
    userType: user.user_type,
    createdAt: new Date(user.created_at).getTime(),
    updatedAt: new Date(user.updated_at).getTime(),
    snsConnections: user.sns_connections || {}
  };
};

export const updateUserProfile = async (userId: string, updates: Partial<UserProfile>): Promise<void> => {
  const updateData: any = {
    updated_at: new Date().toISOString()
  };

  if (updates.email !== undefined) updateData.email = updates.email;
  if (updates.displayName !== undefined) updateData.display_name = updates.displayName;
  if (updates.photoURL !== undefined) updateData.photo_url = updates.photoURL;
  if (updates.userType !== undefined) updateData.user_type = updates.userType;
  if (updates.snsConnections !== undefined) updateData.sns_connections = updates.snsConnections;

  const { error } = await supabase
    .from('users')
    .update(updateData)
    .eq('id', userId);

  if (error) {
    throw error;
  }
};

// Campaign related functions
export const createCampaign = async (campaign: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const now = new Date().toISOString();
  const campaignData = {
    title: campaign.title,
    description: campaign.description,
    image_url: campaign.imageUrl,
    category: campaign.category,
    start_date: campaign.startDate,
    end_date: campaign.endDate,
    status: campaign.status,
    created_by: campaign.createdBy,
    created_at: now,
    updated_at: now
  };

  const { data, error } = await supabase
    .from('campaigns')
    .insert(campaignData)
    .select('id')
    .single();

  if (error) {
    throw error;
  }

  return data.id;
};

export const getCampaigns = async (): Promise<Campaign[]> => {
  const { data, error } = await supabase
    .from('campaigns')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data.map(campaign => ({
    id: campaign.id,
    title: campaign.title,
    description: campaign.description,
    imageUrl: campaign.image_url,
    category: campaign.category,
    startDate: campaign.start_date,
    endDate: campaign.end_date,
    status: campaign.status,
    createdBy: campaign.created_by,
    createdAt: new Date(campaign.created_at).getTime(),
    updatedAt: new Date(campaign.updated_at).getTime()
  }));
};

export const getCampaignById = async (campaignId: string): Promise<Campaign | null> => {
  const { data, error } = await supabase
    .from('campaigns')
    .select('*')
    .eq('id', campaignId)
    .single();

  if (error || !data) {
    return null;
  }

  return {
    id: data.id,
    title: data.title,
    description: data.description,
    imageUrl: data.image_url,
    category: data.category,
    startDate: data.start_date,
    endDate: data.end_date,
    status: data.status,
    createdBy: data.created_by,
    createdAt: new Date(data.created_at).getTime(),
    updatedAt: new Date(data.updated_at).getTime()
  };
};

export const updateCampaign = async (campaignId: string, updates: Partial<Campaign>): Promise<void> => {
  const updateData: any = {
    updated_at: new Date().toISOString()
  };

  if (updates.title !== undefined) updateData.title = updates.title;
  if (updates.description !== undefined) updateData.description = updates.description;
  if (updates.imageUrl !== undefined) updateData.image_url = updates.imageUrl;
  if (updates.category !== undefined) updateData.category = updates.category;
  if (updates.startDate !== undefined) updateData.start_date = updates.startDate;
  if (updates.endDate !== undefined) updateData.end_date = updates.endDate;
  if (updates.status !== undefined) updateData.status = updates.status;
  if (updates.createdBy !== undefined) updateData.created_by = updates.createdBy;

  const { error } = await supabase
    .from('campaigns')
    .update(updateData)
    .eq('id', campaignId);

  if (error) {
    throw error;
  }
};

export const deleteCampaign = async (campaignId: string): Promise<void> => {
  const { error } = await supabase
    .from('campaigns')
    .delete()
    .eq('id', campaignId);

  if (error) {
    throw error;
  }
};

// Review related functions
export const createReview = async (review: Omit<Review, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const now = new Date().toISOString();
  const reviewData = {
    campaign_id: review.campaignId,
    user_id: review.userId,
    content: review.content,
    rating: review.rating,
    images: review.images || [],
    created_at: now,
    updated_at: now
  };

  const { data, error } = await supabase
    .from('reviews')
    .insert(reviewData)
    .select('id')
    .single();

  if (error) {
    throw error;
  }

  return data.id;
};

export const getReviews = async (campaignId: string): Promise<Review[]> => {
  const { data, error } = await supabase
    .from('reviews')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data.map(review => ({
    id: review.id,
    campaignId: review.campaign_id,
    userId: review.user_id,
    content: review.content,
    rating: review.rating,
    images: review.images || [],
    createdAt: new Date(review.created_at).getTime(),
    updatedAt: new Date(review.updated_at).getTime()
  }));
};

export const updateReview = async (reviewId: string, updates: Partial<Review>): Promise<void> => {
  const updateData: any = {
    updated_at: new Date().toISOString()
  };

  if (updates.campaignId !== undefined) updateData.campaign_id = updates.campaignId;
  if (updates.userId !== undefined) updateData.user_id = updates.userId;
  if (updates.content !== undefined) updateData.content = updates.content;
  if (updates.rating !== undefined) updateData.rating = updates.rating;
  if (updates.images !== undefined) updateData.images = updates.images;

  const { error } = await supabase
    .from('reviews')
    .update(updateData)
    .eq('id', reviewId);

  if (error) {
    throw error;
  }
};

export const deleteReview = async (reviewId: string): Promise<void> => {
  const { error } = await supabase
    .from('reviews')
    .delete()
    .eq('id', reviewId);

  if (error) {
    throw error;
  }
};
